import React, { useState } from 'react';
import { ALPHABET_DATA } from '../../core/curriculum/curriculumData';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { PixiGameIsland } from '../../core/games/PixiGameIsland';
import { WordWritingGame } from './WordWritingGame';
import confetti from 'canvas-confetti';
import { seededShuffle, uniqueChoices } from '../../core/games/gameRules';
import { BackIcon, GameIcon, SpeakerIcon } from '../../components/icons/ReadingIcons';

interface GamesViewProps {
  onBack: () => void;
}

type GameType = 'menu' | 'find-letter' | 'match' | 'missing-syllable' | 'write-word';

export const GamesView: React.FC<GamesViewProps> = ({ onBack }) => {
  const [activeGame, setActiveGame] = useState<GameType>('menu');
  const [mascotMessage, setMascotMessage] = useState('Pilih permainan yang ingin kamu mainkan!');
  const [mascotState, setMascotState] = useState<'idle' | 'happy' | 'encourage' | 'celebrate'>('idle');

  // --- Game 1: Cari Huruf State ---
  const [flRound, setFlRound] = useState(0);
  const [flTarget, setFlTarget] = useState(ALPHABET_DATA[1]); // Letter B
  const [flChoices, setFlChoices] = useState<string[]>(['B', 'D', 'P']);
  const [flSuccess, setFlSuccess] = useState(false);

  // --- Game 2: Pasangkan (Match) State ---
  const [selectedUpper, setSelectedUpper] = useState<string | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<string[]>([]);
  const matchItems = [
    { upper: 'A', lower: 'a' },
    { upper: 'B', lower: 'b' },
    { upper: 'M', lower: 'm' },
    { upper: 'S', lower: 's' },
  ];

  // --- Game 3: Suku Kata Hilang State ---
  const [msRound, setMsRound] = useState(0);
  const msQuestions = [
    {
      word: 'SEPEDA',
      before: 'SE',
      missing: 'PE',
      after: 'DA',
      choices: ['PE', 'ME', 'TE'],
      emoji: '🚲',
    },
    {
      word: 'KELAPA',
      before: 'KE',
      missing: 'LA',
      after: 'PA',
      choices: ['LA', 'SA', 'MA'],
      emoji: '🥥',
    },
    {
      word: 'CELANA',
      before: 'CE',
      missing: 'LA',
      after: 'NA',
      choices: ['LA', 'RA', 'DA'],
      emoji: '👖',
    },
    {
      word: 'PELANGI',
      before: 'PE',
      missing: 'LA',
      after: 'NGI',
      choices: ['LA', 'BA', 'KA'],
      emoji: '🌈',
    },
  ];
  const currentMs = msQuestions[msRound % msQuestions.length];
  const [msSuccess, setMsSuccess] = useState(false);

  // --- Cari Huruf Logic ---
  const startFindLetterGame = () => {
    setActiveGame('find-letter');
    setupFindLetterRound(0);
  };

  const setupFindLetterRound = (round: number) => {
    const target = ALPHABET_DATA[(round * 3 + 1) % ALPHABET_DATA.length];
    setFlTarget(target);
    const distractors = seededShuffle(
      ALPHABET_DATA.filter(a => a.letter !== target.letter),
      round + target.letter.charCodeAt(0),
    )
      .slice(0, 2)
      .map(a => a.letter);
    const all = seededShuffle(
      uniqueChoices(target.letter, distractors, 3),
      round + target.letter.charCodeAt(0) + 1,
    );
    setFlChoices(all);
    setFlSuccess(false);
    setMascotState('idle');
    setMascotMessage(`Sentuh gelembung huruf ${target.letter}!`);
    speechProvider.speak(`Cari dan sentuh gelembung huruf ${target.letter}!`, { rate: 0.85 });
  };

  const handleChoiceFindLetter = (choice: string) => {
    if (flSuccess) return;

    if (choice === flTarget.letter) {
      setFlSuccess(true);
      setMascotState('celebrate');
      setMascotMessage(`Yeay, benar! Gelembung huruf ${choice} pecah!`);
      confetti({ particleCount: 40, spread: 70, origin: { y: 0.6 } });
      speechProvider.playChime('success');
      speechProvider.speak(`Benar! Huruf ${choice}!`, { rate: 0.9, pitch: 1.15 });

      appRepository.recordLetterLearned(choice);
    } else {
      setMascotState('encourage');
      setMascotMessage(`Hampir! Itu huruf ${choice}. Ayo cari gelembung ${flTarget.letter}!`);
      speechProvider.playChime('tap');
      speechProvider.speak(`Itu huruf ${choice}. Coba cari ${flTarget.letter}, ya.`, { rate: 0.85 });
    }
  };

  // --- Pasangkan Logic ---
  const startMatchGame = () => {
    setActiveGame('match');
    setMatchedPairs([]);
    setSelectedUpper(null);
    setMascotState('idle');
    setMascotMessage('Pasangkan huruf besar dengan huruf kecil yang sama!');
    speechProvider.speak('Pasangkan huruf besar dengan huruf kecil yang sama!', { rate: 0.85 });
  };

  const handleUpperClick = (upper: string) => {
    if (matchedPairs.includes(upper)) return;
    setSelectedUpper(upper);
    speechProvider.playChime('tap');
    speechProvider.speak(upper, { rate: 0.85 });
  };

  const handleLowerClick = (lower: string) => {
    if (!selectedUpper) {
      setMascotMessage('Pilih huruf besar dulu di sebelah kiri!');
      speechProvider.speak('Pilih huruf besar dulu di kiri ya.', { rate: 0.85 });
      return;
    }

    const pair = matchItems.find(m => m.upper === selectedUpper);
    if (pair && pair.lower === lower) {
      // Matched!
      const newMatched = [...matchedPairs, selectedUpper];
      setMatchedPairs(newMatched);
      setSelectedUpper(null);
      setMascotState('happy');
      setMascotMessage(`Bagus! ${pair.upper} berpasangan dengan ${pair.lower}!`);
      speechProvider.playChime('success');
      speechProvider.speak(`Bagus! Huruf ${pair.upper} dan ${pair.lower}!`, { rate: 0.9 });

      if (newMatched.length === matchItems.length) {
        setMascotState('celebrate');
        setMascotMessage('Hebat sekali! Semua pasangan huruf lengkap!');
        confetti({ particleCount: 50, spread: 80, origin: { y: 0.6 } });
        speechProvider.playChime('celebrate');
      }
    } else {
      // Not match
      setMascotState('encourage');
      setMascotMessage('Hampir cocok! Coba cari huruf kecil yang lain, yuk!');
      speechProvider.playChime('tap');
      speechProvider.speak('Hampir cocok, coba lagi ya.', { rate: 0.85 });
    }
  };

  // --- Suku Kata Hilang Logic ---
  const startMissingSyllableGame = () => {
    setActiveGame('missing-syllable');
    setMsSuccess(false);
    setMascotState('idle');
    setMascotMessage(`Lengkapi kata ${currentMs.word}!`);
    speechProvider.speak(`Ayo lengkapi kata ${currentMs.word}!`, { rate: 0.85 });
  };

  const startWordWritingGame = () => {
    setActiveGame('write-word');
    setMascotState('idle');
    setMascotMessage('Tulis setiap huruf kata di bawahnya, yuk!');
    speechProvider.speak('Ayo tulis setiap huruf kata di bawahnya, yuk!', { rate: 0.85 });
  };

  const handleChooseMissingSyllable = (choice: string) => {
    if (msSuccess) return;

    if (choice === currentMs.missing) {
      setMsSuccess(true);
      setMascotState('celebrate');
      setMascotMessage(`Benar! ${currentMs.before} + ${choice} + ${currentMs.after} jadi ${currentMs.word}!`);
      confetti({ particleCount: 45, spread: 70, origin: { y: 0.6 } });
      speechProvider.playChime('success');
      speechProvider.speakSyllablesAndWord(
        [currentMs.before.toLowerCase(), choice.toLowerCase(), currentMs.after.toLowerCase()],
        currentMs.word
      );
      appRepository.recordWordMastered(currentMs.word);
    } else {
      setMascotState('encourage');
      setMascotMessage(`Hampir! ${choice} belum cocok. Dengarkan bunyinya dan coba lagi!`);
      speechProvider.playChime('tap');
      speechProvider.speak(choice.toLowerCase(), { rate: 0.8 });
    }
  };

  const handleNextMissingSyllable = () => {
    setMsRound(prev => prev + 1);
    setMsSuccess(false);
    setMascotState('idle');
    setMascotMessage('Ayo tebak suku kata berikutnya!');
    const nextItem = msQuestions[(msRound + 1) % msQuestions.length];
    speechProvider.speak(`Ayo lengkapi kata ${nextItem.word}!`, { rate: 0.85 });
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => {
            speechProvider.playChime('tap');
            if (activeGame === 'menu') {
              onBack();
            } else {
              setActiveGame('menu');
              setMascotMessage('Pilih permainan yang ingin kamu mainkan!');
            }
          }}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition"
        >
          <span className="text-sm md:text-base">
            {activeGame === 'menu' ? <><BackIcon className="w-6 h-6" /> Kembali</> : 'Pilih Game Lain'}
          </span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-purple-600 flex items-center gap-2">
          <GameIcon className="w-9 h-9" /> Permainan Belajar
        </h1>

        <div className="w-10"></div>
      </div>

      <div className="max-w-3xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-6">
        <RaraMascot
          state={mascotState}
          message={mascotMessage}
          size="sm"
          onBubbleClick={() => speechProvider.speak(mascotMessage, { rate: 0.88 })}
        />

        {/* Game Menu Selection */}
        {activeGame === 'menu' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Game 1 Card */}
            <button
              onClick={() => {
                speechProvider.playChime('tap');
                startFindLetterGame();
              }}
              className="bg-white rounded-3xl p-6 shadow-lg border-4 border-blue-200 hover:border-blue-400 text-left transition active:scale-95 flex items-center gap-4"
            >
              <div className="w-20 h-20 bg-blue-50 rounded-2xl border-2 border-blue-200 flex items-center justify-center text-4xl shrink-0">
                🫧
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xl font-display font-black text-[#243047]">
                    Pecahkan Huruf (PixiJS)
                  </h3>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Sentuh dan pecahkan gelembung huruf mengapung di kanvas 2D PixiJS!
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-blue-600">
                  <span>Mulai Main</span> <span>▶</span>
                </span>
              </div>
            </button>

            {/* Game 2 Card */}
            <button
              onClick={() => {
                speechProvider.playChime('tap');
                startMatchGame();
              }}
              className="bg-white rounded-3xl p-6 shadow-lg border-4 border-pink-200 hover:border-pink-400 text-left transition active:scale-95 flex items-center gap-4"
            >
              <div className="w-20 h-20 bg-pink-50 rounded-2xl border-2 border-pink-200 flex items-center justify-center text-4xl shrink-0">
                🧩
              </div>
              <div>
                <h3 className="text-xl font-display font-black text-[#243047]">
                  Pasangkan Huruf
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Hubungkan huruf besar dengan huruf kecil yang cocok!
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-pink-600">
                  <span>Mulai Main</span> <span>▶</span>
                </span>
              </div>
            </button>

            {/* Game 3 Card */}
            <button
              onClick={() => {
                speechProvider.playChime('tap');
                startMissingSyllableGame();
              }}
              className="bg-white rounded-3xl p-6 shadow-lg border-4 border-emerald-200 hover:border-emerald-400 text-left transition active:scale-95 flex items-center gap-4"
            >
              <div className="w-20 h-20 bg-emerald-50 rounded-2xl border-2 border-emerald-200 flex items-center justify-center text-4xl shrink-0">
                🎁
              </div>
              <div>
                <h3 className="text-xl font-display font-black text-[#243047]">
                  Kotak Suku Kata Hilang (PixiJS)
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Buka misteri suku kata pada panggung interaktif grafis PixiJS!
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
                  <span>Mulai Main</span> <span>▶</span>
                </span>
              </div>
            </button>

            {/* Game 4 Card */}
            <button
              onClick={() => {
                speechProvider.playChime('tap');
                startWordWritingGame();
              }}
              className="bg-white rounded-3xl p-6 shadow-lg border-4 border-amber-200 hover:border-amber-400 text-left transition active:scale-95 flex items-center gap-4"
            >
              <div className="w-20 h-20 bg-amber-50 rounded-2xl border-2 border-amber-200 flex items-center justify-center text-4xl shrink-0">
                ✏️
              </div>
              <div>
                <h3 className="text-xl font-display font-black text-[#243047]">
                  Tulis Kata
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Gambar setiap huruf kata dan lihat mana yang sudah benar.
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-600">
                  <span>Mulai Main</span> <span>▶</span>
                </span>
              </div>
            </button>
          </div>
        )}

        {/* Game 1: Cari Huruf (Powered by PixiJS Game Island) */}
        {activeGame === 'find-letter' && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-blue-200 flex flex-col items-center text-center">
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047] mb-1">
              Pecahkan Gelembung Huruf <span className="text-blue-600 underline">{flTarget.letter}</span>!
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              Sentuh gelembung yang melayang di kanvas PixiJS di bawah ini
            </p>

            <button
              onClick={() => {
                speechProvider.playChime('tap');
                speechProvider.speak(`Cari gelembung huruf ${flTarget.letter}`, { rate: 0.85 });
              }}
              className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-bold text-sm mb-4 border border-blue-200 active:scale-95 transition"
            >
              <SpeakerIcon className="w-5 h-5" />
              <span>Ulangi Bunyi Huruf ({flTarget.letter})</span>
            </button>

            {/* PixiJS Island Canvas */}
            <PixiGameIsland
              key={`${flRound}-${flTarget.letter}`}
              gameMode="find-letter"
              targetValue={flTarget.letter}
              choices={flChoices}
              onCorrectChoice={handleChoiceFindLetter}
              onWrongChoice={handleChoiceFindLetter}
              className="my-2"
            />

            {/* HTML Fallback Button Grid for accessibility */}
            <div className="flex gap-3 justify-center mt-3">
              {flChoices.map(c => (
                <button
                  key={c}
                  onClick={() => handleChoiceFindLetter(c)}
                  className="px-5 py-2.5 rounded-2xl bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-900 font-display font-black text-xl active:scale-90"
                >
                  {c}
                </button>
              ))}
            </div>

            {flSuccess && (
              <button
                onClick={() => {
                  speechProvider.playChime('tap');
                  setFlRound(prev => prev + 1);
                  setupFindLetterRound(flRound + 1);
                }}
                className="mt-6 px-8 py-3.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-bold text-lg rounded-2xl shadow-lg flex items-center gap-2 transition"
              >
                <span>Lanjut Huruf Berikutnya</span>
                <span>➡️</span>
              </button>
            )}
          </div>
        )}

        {/* Game 2: Pasangkan View */}
        {activeGame === 'match' && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-pink-200 flex flex-col items-center text-center">
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047] mb-2">
              Pasangkan Huruf Besar & Kecil
            </h2>
            <p className="text-xs md:text-sm text-gray-500 mb-6">
              1. Sentuh huruf BESAR di kiri &nbsp; 2. Lalu sentuh huruf KECIL di kanan
            </p>

            <div className="grid grid-cols-2 gap-8 md:gap-16 w-full max-w-md my-2">
              {/* Left Column: Uppercase */}
              <div className="flex flex-col gap-3">
                <div className="text-xs font-bold text-gray-400">HURUF BESAR</div>
                {matchItems.map(item => {
                  const isDone = matchedPairs.includes(item.upper);
                  const isSelected = selectedUpper === item.upper;
                  return (
                    <button
                      key={item.upper}
                      disabled={isDone}
                      onClick={() => handleUpperClick(item.upper)}
                      className={`h-16 rounded-2xl font-display font-black text-2xl border-3 transition-all flex items-center justify-center ${
                        isDone
                          ? 'bg-gray-100 border-gray-200 text-gray-400 opacity-60'
                          : isSelected
                          ? 'bg-pink-500 text-white border-pink-600 scale-105 shadow-md ring-4 ring-pink-200'
                          : 'bg-pink-50/80 border-pink-200 text-pink-900 hover:bg-pink-100 active:scale-95'
                      }`}
                    >
                      {item.upper} {isDone && '✓'}
                    </button>
                  );
                })}
              </div>

              {/* Right Column: Lowercase (shuffled display) */}
              <div className="flex flex-col gap-3">
                <div className="text-xs font-bold text-gray-400">HURUF KECIL</div>
                {[matchItems[2], matchItems[0], matchItems[3], matchItems[1]].map(item => {
                  const isDone = matchedPairs.includes(item.upper);
                  return (
                    <button
                      key={item.lower}
                      disabled={isDone}
                      onClick={() => handleLowerClick(item.lower)}
                      className={`h-16 rounded-2xl font-display font-extrabold text-2xl border-3 transition-all flex items-center justify-center ${
                        isDone
                          ? 'bg-gray-100 border-gray-200 text-gray-400 opacity-60'
                          : 'bg-purple-50/80 border-purple-200 text-purple-900 hover:bg-purple-100 active:scale-95'
                      }`}
                    >
                      {item.lower} {isDone && '✓'}
                    </button>
                  );
                })}
              </div>
            </div>

            {matchedPairs.length === matchItems.length && (
              <button
                onClick={() => {
                  speechProvider.playChime('tap');
                  startMatchGame();
                }}
                className="mt-6 px-8 py-3.5 bg-pink-500 hover:bg-pink-600 active:scale-95 text-white font-bold text-lg rounded-2xl shadow-lg"
              >
                🎉 Main Lagi
              </button>
            )}
          </div>
        )}

        {/* Game 3: Suku Kata Hilang (Powered by PixiJS Game Island) */}
        {activeGame === 'missing-syllable' && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-emerald-200 flex flex-col items-center text-center">
            <div className="text-5xl mb-2">{currentMs.emoji}</div>
            <h2 className="text-xl md:text-2xl font-display font-black text-[#243047] mb-2">
              Lengkapi Kata: {currentMs.word}
            </h2>

            {/* Word with Missing Blank */}
            <div className="flex items-center justify-center gap-3 my-2">
              <span className="px-4 py-2 bg-emerald-50 rounded-2xl border-2 border-emerald-300 font-display font-black text-2xl md:text-3xl text-emerald-950">
                {currentMs.before}
              </span>
              <span
                className={`px-4 py-2 rounded-2xl border-3 font-display font-black text-2xl md:text-3xl transition-all ${
                  msSuccess
                    ? 'bg-emerald-500 text-white border-emerald-600 scale-105'
                    : 'bg-amber-100 border-dashed border-amber-400 text-amber-800'
                }`}
              >
                {msSuccess ? currentMs.missing : '?'}
              </span>
              <span className="px-4 py-2 bg-emerald-50 rounded-2xl border-2 border-emerald-300 font-display font-black text-2xl md:text-3xl text-emerald-950">
                {currentMs.after}
              </span>
            </div>

            {/* PixiJS Mystery Stage */}
            <PixiGameIsland
              key={`${msRound}-${currentMs.word}`}
              gameMode="missing-syllable"
              targetValue={currentMs.missing}
              choices={currentMs.choices}
              onCorrectChoice={handleChooseMissingSyllable}
              onWrongChoice={handleChooseMissingSyllable}
              className="my-3"
            />

            {msSuccess && (
              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={() => {
                    speechProvider.playChime('tap');
                    handleNextMissingSyllable();
                  }}
                  className="px-8 py-3.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-bold text-lg rounded-2xl shadow-lg flex items-center gap-2"
                >
                  <span>Kata Berikutnya</span>
                  <span>➡️</span>
                </button>
              </div>
            )}
          </div>
        )}

        {activeGame === 'write-word' && (
          <WordWritingGame
            onExit={() => {
              speechProvider.playChime('tap');
              setActiveGame('menu');
              setMascotState('idle');
              setMascotMessage('Pilih permainan yang ingin kamu mainkan!');
            }}
          />
        )}
      </div>
    </div>
  );
};
